import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Get API URL from environment variable or use default
const API_URL = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// Fetch gap brands
export const fetchGapBrands = createAsyncThunk(
  'gapBrands/fetchGapBrands',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_URL}/api/addGapBrand/list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        mode: 'cors'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || `HTTP error! status: ${response.status}`);
      }

      if (data && data.success) {
        return data.brands || [];
      } else {
        throw new Error(data?.message || 'API returned unsuccessful response');
      }

    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch gap brands');
    }
  }
);

// Add new gap brand
export const addGapBrand = createAsyncThunk(
  'gapBrands/addGapBrand',
  async ({ brandName, brandUrl, userBrandUrl, selectedVerticals, selectedCountries, csvBacklinks, skipSemrush, verticalMode, subVerticals }, { rejectWithValue }) => {
    try {
      const payload = {
        brandName,
        brandUrl,
        userBrandUrl,
        selectedVerticals: selectedVerticals || ['Casino'],
        selectedCountries: selectedCountries || ['UK']
      };

      // Trading & Investment. The server re-checks that the account has the
      // vertical and replaces selectedVerticals above when it does, so a client
      // that does not send this is unaffected and one that lies gets a 403.
      if (verticalMode === 'trading') {
        payload.verticalMode = 'trading';
        if (Array.isArray(subVerticals) && subVerticals.length > 0) {
          payload.subVerticals = subVerticals;
        }
      }

      // Admin-only referring-domain CSV. The server re-checks the admin email
      // and ignores these fields for anyone else, so sending them is harmless.
      if (skipSemrush && Array.isArray(csvBacklinks) && csvBacklinks.length > 0) {
        payload.csvBacklinks = csvBacklinks;
        payload.skipSemrush = true;
      }

      const response = await fetch(`${API_URL}/api/addGapBrand/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        // Check for limit exceeded error
        if (data.error === 'GAP_LIMIT_EXCEEDED') {
          return rejectWithValue({
            error: 'GAP_LIMIT_EXCEEDED',
            message: data.message,
            data: data.limitData
          });
        }
        // This route reports failures as `error`, not `message` — reading only
        // `message` is why a plain-English reason ("already in your Affiliate
        // Gap analysis") reached the user as a bare "HTTP error! status: 400".
        throw new Error(data?.error || data?.message || `HTTP error! status: ${response.status}`);
      }

      if (data && data.success) {
        return {
          brand: data.brand,
          message: data.message,
          processing: data.processing
        };
      } else {
        throw new Error(data?.error || data?.message || 'API returned unsuccessful response');
      }

    } catch (err) {
      return rejectWithValue(err.message || 'Failed to add gap brand');
    }
  }
);

// Poll gap brand processing status
export const pollGapBrandStatus = createAsyncThunk(
  'gapBrands/pollGapBrandStatus',
  async ({ brandId, brandUrl }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_URL}/api/addGapBrand/status/${brandId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        mode: 'cors'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        brandId,
        brandUrl,
        processingStatus: data.brand?.processingStatus || data.processingStatus,
        isCompleted: data.brand?.isCompleted || data.isCompleted,
        // V2 pipeline real-time progress (null for legacy processing)
        stageLabel: data.brand?.stageLabel || null,
        percent: typeof data.brand?.percent === 'number' ? data.brand.percent : null,
        // Background live-affiliate verification ("bulb")
        liveCheckStatus: data.brand?.liveCheckStatus || null,
        // Live Common/Gap counts — drop as the live check removes non-promoting
        // affiliates, so the card's numbers tick down without a page refresh.
        commonAffiliates: data.brand?.common_affiliates,
        gapAffiliates: data.brand?.gap_analysis_affiliates
      };

    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch processing status');
    }
  }
);

const initialState = {
  gapBrands: [],
  loading: false,
  error: null,
  lastFetchTime: null,
  // Processing status tracking
  processingBrands: {}, // { brandUrl: { status: '1', step: 'Fetching backlinks...', brandName: 'Example', isActive: true } }
  isPolling: false,
  pollingIntervals: {},
  // Form state
  formData: {
    brandName: '',
    spyUrl: '',
    userBrandUrl: '',
    country: ''
  },
  isAdding: false,
  addError: null,
  successMessage: null
};

const gapBrandsSlice = createSlice({
  name: 'gapBrands',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAddError: (state) => {
      state.addError = null;
    },
    clearSuccess: (state) => {
      state.successMessage = null;
    },
    updateFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    resetFormData: (state) => {
      state.formData = { brandName: '', spyUrl: '', userBrandUrl: '', country: '' };
    },
    // Start polling for a gap brand. initialStatus avoids a false "step 1"
    // flash when polling (re)starts for a brand that is already mid-pipeline
    // or already completed with only the live check still running.
    startGapPolling: (state, action) => {
      const { brandUrl, brandName, brandId, initialStatus } = action.payload;

      state.processingBrands[brandUrl] = {
        status: initialStatus || '1',
        // 'completed' / '8' = pipeline already done (instant copy) — only the
        // background live-affiliate check is running.
        step: (initialStatus === 'completed' || parseInt(initialStatus) === 8)
          ? 'Verifying live affiliates...' : 'Analyzing brand...',
        isActive: true,
        brandName: brandName || brandUrl,
        brandId: brandId,
        startTime: new Date().toISOString()
      };
      state.isPolling = true;
    },
    // Stop polling for a gap brand
    stopGapPolling: (state, action) => {
      const { brandUrl } = action.payload;

      if (state.processingBrands[brandUrl]) {
        state.processingBrands[brandUrl].isActive = false;
        state.processingBrands[brandUrl].endTime = new Date().toISOString();
      }

      // Check if any brands are still being polled
      const hasActivePolling = Object.values(state.processingBrands).some(brand => brand.isActive);
      state.isPolling = hasActivePolling;
    },
    // Update processing step message
    updateGapProcessingStep: (state, action) => {
      const { brandUrl, step, status } = action.payload;
      if (state.processingBrands[brandUrl]) {
        state.processingBrands[brandUrl].step = step;
        if (status) {
          state.processingBrands[brandUrl].status = status;
        }
      }
    },
    // Set polling interval ID for cleanup
    setGapPollingInterval: (state, action) => {
      const { brandUrl, intervalId } = action.payload;
      state.pollingIntervals[brandUrl] = intervalId;
    },
    // Clear polling interval
    clearGapPollingInterval: (state, action) => {
      const { brandUrl } = action.payload;
      if (state.pollingIntervals[brandUrl]) {
        delete state.pollingIntervals[brandUrl];
      }
    },
    // Clean up completed processing brands
    cleanupGapProcessingBrands: (state) => {
      const cutoffTime = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      Object.keys(state.processingBrands).forEach(brandUrl => {
        const brand = state.processingBrands[brandUrl];
        if (!brand.isActive && brand.endTime && brand.endTime < cutoffTime) {
          delete state.processingBrands[brandUrl];
        }
      });
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch gap brands cases
      .addCase(fetchGapBrands.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGapBrands.fulfilled, (state, action) => {
        state.loading = false;
        state.gapBrands = action.payload;
        state.error = null;
        state.lastFetchTime = new Date().toISOString();
      })
      .addCase(fetchGapBrands.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.gapBrands = [];
      })
      // Poll gap brand status cases
      .addCase(pollGapBrandStatus.fulfilled, (state, action) => {
        const { brandId, brandUrl, processingStatus, isCompleted, stageLabel, percent, liveCheckStatus, commonAffiliates, gapAffiliates } = action.payload;
        const pipelineDone = isCompleted || processingStatus === 'completed' || parseInt(processingStatus) >= 8;
        const pipelineFailed = processingStatus === 'failed' || processingStatus === 'error';
        // The live-affiliate verification keeps running AFTER the pipeline
        // completes — keep polling so the bulb clears in real time.
        const liveCheckActive = liveCheckStatus === 'pending' || liveCheckStatus === 'running';

        if (state.processingBrands[brandUrl]) {
          const info = state.processingBrands[brandUrl];
          info.status = processingStatus;
          info.liveCheckStatus = liveCheckStatus;
          if (percent !== null && percent !== undefined) info.percent = percent;

          // User-friendly step messages (non-technical)
          const stepMessages = {
            '1': 'Analyzing brand...',
            '2': 'Gathering data...',
            '3': 'Finding opportunities...',
            '4': 'Identifying partners...',
            '5': 'Building insights...',
            '6': 'Analyzing trends...',
            '7': 'Preparing report...',
            '8': 'Finalizing results...',
            'completed': 'Analysis complete!',
            'failed': 'Processing failed. Please try again.'
          };

          if (pipelineDone && liveCheckActive) {
            info.step = 'Verifying live affiliates...';
            info.percent = 100;
          } else {
            // V2 pipelines report a live stage label from brand_jobs;
            // fall back to the legacy numeric mapping.
            info.step = stageLabel || stepMessages[processingStatus] || `Processing step ${processingStatus}...`;
          }

          // Stop polling only once the pipeline AND the live check are done
          if ((pipelineDone && !liveCheckActive) || pipelineFailed) {
            info.step = stepMessages[processingStatus] || info.step;
            info.isActive = false;
            info.endTime = new Date().toISOString();

            // Check if any brands are still being polled
            const hasActivePolling = Object.values(state.processingBrands).some(brand => brand.isActive);
            state.isPolling = hasActivePolling;
          }
        }

        // Keep the brand row in the list in sync so gapCard's bulb and
        // status render correctly without a refetch
        const brandIndex = state.gapBrands.findIndex(b => b.id === brandId || b.brand_url === brandUrl);
        if (brandIndex !== -1) {
          state.gapBrands[brandIndex].processing_status = processingStatus;
          state.gapBrands[brandIndex].live_check_status = liveCheckStatus;
          // Tick the Common/Gap numbers down live as the check removes affiliates.
          if (commonAffiliates !== undefined && commonAffiliates !== null) {
            state.gapBrands[brandIndex].common_affiliates = commonAffiliates;
          }
          if (gapAffiliates !== undefined && gapAffiliates !== null) {
            state.gapBrands[brandIndex].gap_analysis_affiliates = gapAffiliates;
          }
        }
      })
      .addCase(pollGapBrandStatus.rejected, (state, action) => {
        console.error('Gap polling error:', action.payload);
      })
      // Add gap brand cases
      .addCase(addGapBrand.pending, (state) => {
        state.isAdding = true;
        state.addError = null;
      })
      .addCase(addGapBrand.fulfilled, (state, action) => {
        state.isAdding = false;
        state.addError = null;
        state.successMessage = action.payload.message || 'Gap brand added successfully';
        // Reset form data on success
        state.formData = { brandName: '', spyUrl: '', userBrandUrl: '', country: '' };
        // Add the new brand to the list
        if (action.payload.brand) {
          state.gapBrands.unshift(action.payload.brand);
        }
      })
      .addCase(addGapBrand.rejected, (state, action) => {
        state.isAdding = false;
        state.addError = action.payload?.message || action.payload || 'Failed to add gap brand';
      });
  }
});

// Action creators
export const {
  clearError,
  clearAddError,
  clearSuccess,
  updateFormData,
  resetFormData,
  startGapPolling,
  stopGapPolling,
  updateGapProcessingStep,
  setGapPollingInterval,
  clearGapPollingInterval,
  cleanupGapProcessingBrands
} = gapBrandsSlice.actions;

// Thunk for managing polling lifecycle
export const startGapPollingWithCleanup = (brandData) => (dispatch, getState) => {
  const { brandUrl, brandName, brandId, initialStatus } = brandData;

  // Start polling in state
  dispatch(startGapPolling({ brandUrl, brandName, brandId, initialStatus }));

  // Set up polling interval
  const intervalId = setInterval(() => {
    const state = getState();
    const processingInfo = state.gapBrands?.processingBrands[brandUrl];

    // Only poll if still active
    if (processingInfo?.isActive) {
      dispatch(pollGapBrandStatus({ brandId, brandUrl }));
    } else {
      // Clean up interval if no longer active
      clearInterval(intervalId);
      dispatch(clearGapPollingInterval({ brandUrl }));
    }
  }, 5000); // Poll every 5 seconds

  // Store interval ID for cleanup
  dispatch(setGapPollingInterval({ brandUrl, intervalId }));

  // Immediate first poll so stage/percent/bulb state land without a 5s wait
  dispatch(pollGapBrandStatus({ brandId, brandUrl }));

  return intervalId;
};

// Thunk for stopping polling with cleanup
export const stopGapPollingWithCleanup = (brandUrl) => (dispatch, getState) => {
  const state = getState();
  const intervalId = state.gapBrands?.pollingIntervals[brandUrl];

  // Clear the interval
  if (intervalId) {
    clearInterval(intervalId);
    dispatch(clearGapPollingInterval({ brandUrl }));
  }

  // Stop polling in state
  dispatch(stopGapPolling({ brandUrl }));
};

// Selectors
export const selectGapBrands = (state) => state.gapBrands?.gapBrands || [];
export const selectGapBrandsLoading = (state) => state.gapBrands?.loading || false;
export const selectGapBrandsError = (state) => state.gapBrands?.error || null;
export const selectGapProcessingBrands = (state) => state.gapBrands?.processingBrands || {};
export const selectGapIsPolling = (state) => state.gapBrands?.isPolling || false;
export const selectGapFormData = (state) => state.gapBrands?.formData || { brandName: '', spyUrl: '', userBrandUrl: '', country: '' };
export const selectGapIsAdding = (state) => state.gapBrands?.isAdding || false;
export const selectGapAddError = (state) => state.gapBrands?.addError || null;
export const selectGapSuccessMessage = (state) => state.gapBrands?.successMessage || null;

// Selector for getting processing status of a specific gap brand
export const selectGapBrandProcessingStatus = (brandUrl) => (state) => {
  return state.gapBrands?.processingBrands[brandUrl] || null;
};

export default gapBrandsSlice.reducer;
